<!-- B4 机构门店管理 -->
<template>
  <div>
    <el-card shadow="never">
      <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="68px">
        <el-form-item label="门店名称" prop="name">
          <el-input
            v-model="queryParams.name"
            class="!w-220px"
            clearable
            placeholder="请输入门店名称"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="城市" prop="city">
          <el-input
            v-model="queryParams.city"
            class="!w-220px"
            clearable
            placeholder="请输入城市"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="queryParams.status" class="!w-220px" clearable placeholder="全部">
            <el-option label="营业中" :value="1" />
            <el-option label="关闭" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery">
            <Icon class="mr-5px" icon="ep:search" />搜索
          </el-button>
          <el-button @click="resetQuery">
            <Icon class="mr-5px" icon="ep:refresh" />重置
          </el-button>
          <el-button type="primary" plain @click="openForm('create')">
            <Icon class="mr-5px" icon="ep:plus" />新增门店
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="list" stripe>
        <el-table-column label="门店名称" min-width="140" prop="name" show-overflow-tooltip />
        <el-table-column label="城市" width="100" prop="city" />
        <el-table-column label="地址" min-width="200" show-overflow-tooltip>
          <template #default="{ row }">
            {{ [row.province, row.city, row.district, row.address].filter(Boolean).join('') }}
          </template>
        </el-table-column>
        <el-table-column label="电话" width="130" prop="phone" />
        <el-table-column label="营业时间" width="130" prop="businessHours" />
        <el-table-column label="状态" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
              {{ row.status === 1 ? '营业中' : '关闭' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="170" prop="createTime" :formatter="dateFormatter" />
        <el-table-column label="操作" width="210" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openForm('update', row.id)">编辑</el-button>
            <el-button link type="primary" @click="scheduleRef?.open(row)">排班</el-button>
            <el-button link type="danger" @click="handleDelete(row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </el-card>

    <el-dialog v-model="formVisible" :title="formTitle" width="640px" destroy-on-close>
      <el-form ref="formRef" v-loading="formLoading" :model="formData" :rules="formRules" label-width="96px">
        <el-form-item label="门店名称" prop="name">
          <el-input
            v-model="formData.name"
            maxlength="100"
            show-word-limit
            placeholder="请输入门店名称（最多 100 个字符）"
          />
        </el-form-item>
        <el-form-item label="联系电话" prop="phone">
          <el-input
            v-model="formData.phone"
            maxlength="20"
            show-word-limit
            placeholder="手机号或座机，如 13800138000 / 025-88886666"
          />
        </el-form-item>
        <el-form-item label="省市区" prop="areaPath">
          <el-cascader
            v-model="formData.areaPath"
            class="!w-full"
            :options="areaTree"
            :props="storeAreaCascaderProps"
            clearable
            filterable
            placeholder="请选择省 / 市 / 区（区可选）"
            @change="onAreaPathChange"
          />
        </el-form-item>
        <el-form-item label="详细地址" prop="address">
          <el-input
            v-model="formData.address"
            maxlength="300"
            show-word-limit
            placeholder="街道门牌号"
          />
        </el-form-item>
        <el-form-item label="门店封面">
          <UploadImg v-model="formData.coverImage" directory="o2o/store/cover" height="96px" width="96px">
            <template #tip>
              <span class="text-12px text-gray-500">选填；用户到店预约时展示</span>
            </template>
          </UploadImg>
        </el-form-item>
        <el-form-item label="营业时间">
          <el-input v-model="formData.businessHours" placeholder="如 09:00-18:00" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="formData.sort" :min="0" controls-position="right" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="formData.status">
            <el-radio :value="1">营业中</el-radio>
            <el-radio :value="0">关闭</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="formVisible = false">取 消</el-button>
        <el-button :loading="formLoading" type="primary" @click="submitForm">确 定</el-button>
      </template>
    </el-dialog>
  </div>
  <StoreScheduleDialog ref="scheduleRef" />
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import { UploadImg } from '@/components/UploadFile'
import { getAreaTree } from '@/api/merchant/system/area'
import StoreScheduleDialog from './StoreScheduleDialog.vue'
import {
  createO2oMerchantStore,
  deleteO2oMerchantStore,
  getO2oMerchantStore,
  getO2oMerchantStorePage,
  updateO2oMerchantStore,
  type O2oStoreMerchantRespVO,
  type O2oStoreMerchantSaveReqVO
} from '@/api/merchant/o2o/store'
import {
  findStoreAreaPathByNames,
  resolveStoreAreaByPath,
  storeAreaCascaderProps
} from '@/views/o2o/utils/storeArea'

defineOptions({ name: 'MerchantO2oStore' })

const message = useMessage()
const scheduleRef = ref()

const loading = ref(true)
const list = ref<O2oStoreMerchantRespVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  name: undefined as string | undefined,
  city: undefined as string | undefined,
  status: undefined as number | undefined
})
const queryFormRef = ref()
const areaTree = ref<any[]>([])

const formVisible = ref(false)
const formTitle = ref('')
const formLoading = ref(false)
const formType = ref<'create' | 'update'>('create')
const formRef = ref()
/** areaPath 仅前端级联用，提交前剥离 */
type StoreFormModel = O2oStoreMerchantSaveReqVO & { areaPath?: number[] }
const formData = ref<StoreFormModel>({
  name: '',
  province: '',
  city: '',
  address: '',
  status: 1,
  sort: 0,
  areaPath: undefined
})
/** 与 o2o_store.name varchar(100)、phone varchar(20) 对齐 */
const STORE_NAME_MAX = 100
/** 手机 11 位，或座机区号+号码（可带 -），与后端 Pattern 一致 */
const STORE_PHONE_REG = /^(1\d{10}|0\d{2,3}-?\d{7,8})$/
const validateStorePhone = (_rule: unknown, value: string, callback: (e?: Error) => void) => {
  const phone = (value ?? '').trim()
  if (!phone) {
    callback()
    return
  }
  if (STORE_PHONE_REG.test(phone)) {
    callback()
    return
  }
  callback(new Error('联系电话格式不正确，请填写手机号或座机号'))
}
const validateAreaPath = (_rule: unknown, value: number[] | undefined, callback: (e?: Error) => void) => {
  if (!value?.length || value.length < 2) {
    callback(new Error('请选择省市区（至少选到市）'))
    return
  }
  callback()
}
const formRules = {
  name: [
    { required: true, message: '门店名称不能为空', trigger: 'blur' },
    { max: STORE_NAME_MAX, message: `门店名称不能超过 ${STORE_NAME_MAX} 个字符`, trigger: 'blur' }
  ],
  phone: [{ validator: validateStorePhone, trigger: 'blur' }],
  areaPath: [{ required: true, validator: validateAreaPath, trigger: 'change' }],
  address: [{ required: true, message: '详细地址不能为空', trigger: 'blur' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }]
}

const ensureAreaTree = async () => {
  if (areaTree.value.length) return
  areaTree.value = (await getAreaTree()) || []
}

const applyAreaNames = (path?: number[]) => {
  const names = resolveStoreAreaByPath(areaTree.value, path)
  formData.value.province = names.province
  formData.value.city = names.city
  formData.value.district = names.district
}

const onAreaPathChange = (path?: number[]) => {
  applyAreaNames(path)
}

const syncAreaPathFromNames = () => {
  const path = findStoreAreaPathByNames(
    areaTree.value,
    formData.value.province,
    formData.value.city,
    formData.value.district
  )
  formData.value.areaPath = path
  if (path) applyAreaNames(path)
}

const getList = async () => {
  loading.value = true
  try {
    const data = await getO2oMerchantStorePage({ ...queryParams })
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const resetFormData = () => {
  formData.value = {
    name: '',
    phone: '',
    province: '',
    city: '',
    district: '',
    address: '',
    businessHours: '',
    status: 1,
    sort: 0,
    areaPath: undefined
  }
}

const openForm = async (type: 'create' | 'update', id?: number) => {
  formVisible.value = true
  formType.value = type
  formTitle.value = type === 'create' ? '新增门店' : '编辑门店'
  resetFormData()
  formLoading.value = true
  try {
    await ensureAreaTree()
    if (type === 'update' && id) {
      const detail = await getO2oMerchantStore(id)
      formData.value = {
        id: detail.id,
        name: detail.name,
        phone: detail.phone,
        province: detail.province || '',
        city: detail.city || '',
        district: detail.district,
        address: detail.address || '',
        coverImage: detail.coverImage,
        businessHours: detail.businessHours,
        sort: detail.sort ?? 0,
        status: detail.status,
        areaPath: undefined
      }
      syncAreaPathFromNames()
      if ((detail.province || detail.city) && !formData.value.areaPath?.length) {
        message.warning('原省市区无法匹配地区库，请重新选择')
      }
    }
  } finally {
    formLoading.value = false
  }
}

const submitForm = async () => {
  if (typeof formData.value.phone === 'string') {
    formData.value.phone = formData.value.phone.trim()
  }
  applyAreaNames(formData.value.areaPath)
  await formRef.value?.validate()
  formLoading.value = true
  try {
    const { areaPath: _areaPath, ...payload } = formData.value
    if (formType.value === 'create') {
      await createO2oMerchantStore(payload)
    } else {
      await updateO2oMerchantStore(payload)
    }
    message.success('保存成功')
    formVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}

const handleDelete = async (id: number) => {
  try {
    await message.delConfirm('确认删除该门店？')
  } catch {
    return
  }
  await deleteO2oMerchantStore(id)
  message.success('删除成功')
  await getList()
}

onMounted(() => getList())
</script>
